import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'origens-theme'

type ThemeContextValue = {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
  cyclePreference: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStoredPreference(fallback: ThemePreference = 'system'): ThemePreference {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    if (value === 'light' || value === 'dark' || value === 'system') return value
  } catch {
    // ignore
  }
  return fallback
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolve(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference
}

function applyResolved(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.classList.toggle('light', resolved === 'light')
  root.style.colorScheme = resolved
}

export function ThemeProvider({
  children,
  defaultPreference = 'system',
}: {
  children: ReactNode
  defaultPreference?: ThemePreference
}) {
  const [preference, setPreferenceState] = useState<ThemePreference>(defaultPreference)
  const [resolved, setResolved] = useState<ResolvedTheme>('light')

  const syncFromPreference = useCallback((next: ThemePreference) => {
    const nextResolved = resolve(next)
    setResolved(nextResolved)
    applyResolved(nextResolved)
  }, [])

  useEffect(() => {
    const initial = readStoredPreference(defaultPreference)
    setPreferenceState(initial)
    syncFromPreference(initial)

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onSystemChange = () => {
      if (readStoredPreference(defaultPreference) === 'system') syncFromPreference('system')
    }
    media.addEventListener('change', onSystemChange)

    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return
      const next = readStoredPreference(defaultPreference)
      setPreferenceState(next)
      syncFromPreference(next)
    }
    window.addEventListener('storage', onStorage)

    return () => {
      media.removeEventListener('change', onSystemChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [defaultPreference, syncFromPreference])

  const setPreference = useCallback(
    (next: ThemePreference) => {
      setPreferenceState(next)
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // ignore
      }
      syncFromPreference(next)
    },
    [syncFromPreference],
  )

  const cyclePreference = useCallback(() => {
    const order: ThemePreference[] = ['system', 'light', 'dark']
    setPreferenceState((current) => {
      const index = order.indexOf(current)
      const next = order[(index + 1) % order.length] ?? 'system'
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // ignore
      }
      syncFromPreference(next)
      return next
    })
  }, [syncFromPreference])

  const value = useMemo(
    () => ({ preference, resolved, setPreference, cyclePreference }),
    [preference, resolved, setPreference, cyclePreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return ctx
}

function preferenceLabel(preference: ThemePreference): string {
  switch (preference) {
    case 'light':
      return 'Light'
    case 'dark':
      return 'Dark'
    default:
      return 'System'
  }
}

function ThemeSegmentedControl({
  preference,
  setPreference,
  iconClassName = 'h-4 w-4',
  compact = false,
}: {
  preference: ThemePreference
  setPreference: (preference: ThemePreference) => void
  iconClassName?: string
  compact?: boolean
}) {
  const options: Array<{ value: ThemePreference; label: string; icon: ReactNode }> = [
    { value: 'system', label: 'System', icon: <Monitor className={iconClassName} /> },
    { value: 'light', label: 'Light', icon: <Sun className={iconClassName} /> },
    { value: 'dark', label: 'Dark', icon: <Moon className={iconClassName} /> },
  ]
  const buttonSize = compact ? 'size-6' : 'size-7'
  return (
    <div
      role="group"
      aria-label="Color theme"
      className="inline-flex items-center gap-0.5 rounded-full border border-[var(--border)] bg-[var(--surface)] p-1"
    >
      {options.map((option) => {
        const active = preference === option.value
        return (
          <button
            key={option.value}
            type="button"
            aria-label={option.label}
            aria-pressed={active}
            title={option.label}
            onClick={() => setPreference(option.value)}
            className={
              active
                ? `inline-flex ${buttonSize} items-center justify-center rounded-full bg-[var(--muted)] text-[var(--ink)]`
                : `inline-flex ${buttonSize} items-center justify-center rounded-full text-[var(--ink-muted)] hover:text-[var(--ink)]`
            }
          >
            {option.icon}
          </button>
        )
      })}
    </div>
  )
}

/** Compact control for landing chrome / floating corners. */
export function ThemeToggle({ className }: { className?: string }) {
  const { preference, setPreference } = useTheme()
  return (
    <div className={className}>
      <span className="sr-only">Theme: {preferenceLabel(preference)}</span>
      <ThemeSegmentedControl
        preference={preference}
        setPreference={setPreference}
        iconClassName="h-3.5 w-3.5"
        compact
      />
    </div>
  )
}

/** Menu-row control for the portal account dropdown. */
export function ThemeMenuItem({ className }: { className?: string }) {
  const { preference, setPreference } = useTheme()
  return (
    <div className={className}>
      <span className="text-sm font-medium text-[var(--ink)]">Theme</span>
      <ThemeSegmentedControl preference={preference} setPreference={setPreference} />
    </div>
  )
}
